import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { TranslateModule } from '@ngx-translate/core';

import { DiagnosticRating, HeapEventName } from '@malou-io/package-utils';

import type { Diagnostic } from ':shared/models';
import type { DiagnosticKeyword } from ':shared/models/keyword';

import { PartnerThemeContext } from ':core/context/partner-theme.context';
import { DiagnosticHttpService } from ':core/http-services/diagnostic.http-service';
import { RatingChipComponent } from ':modules/rating-chip/rating-chip.component';
import { RankingCompetitorsKeywordListComponent } from ':modules/restaurant-diagnostic-keywords/ranking-competitors-keyword-list/ranking-competitors-keyword-list.component';
import { Icon } from ':shared/enums';
import { TrackByFunctionFactory } from ':shared/helpers/track-by-functions';
import { ImagePathResolverPipe, SentenceCasePipe } from ':shared/pipes';
import { getUserIdentityFromLocalStorage } from ':shared/utils/manage-user-uuid';

interface MaloupeKeyword {
    keyword: DiagnosticKeyword;
    text?: string | null;
    rating: DiagnosticRating;
    displayedRating: string;
    restaurantName: string;
    address: string;
    placeId: string;
}

@Component({
    selector: 'app-restaurant-diagnostic-keywords',
    imports: [NgTemplateOutlet, MatTableModule, TranslateModule, RatingChipComponent, ImagePathResolverPipe, SentenceCasePipe],
    templateUrl: './restaurant-diagnostic-keywords.component.html',
    styleUrl: './restaurant-diagnostic-keywords.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RestaurantDiagnosticKeywordsComponent {
    readonly keywords = input.required<MaloupeKeyword[]>();
    readonly diagnosticData = input.required<{ diagnostic: Diagnostic; receiverEmail: string }>();

    readonly keywordsDiagnosticRating = computed(() => this.diagnosticData().diagnostic.getKeywordsRating() ?? DiagnosticRating.BAD);
    readonly partnerTheme = inject(PartnerThemeContext).activeTheme;
    DiagnosticRating = DiagnosticRating;
    Icon = Icon;

    private readonly _dialog = inject(MatDialog);
    private readonly _diagnosticHttpService = inject(DiagnosticHttpService);

    readonly displayedColumns: string[] = ['keywords', 'googlePosition', 'competitors'];
    readonly trackByIdFn = TrackByFunctionFactory.get('_id');

    readonly datasource = computed(() => {
        const keywords = this.keywords();
        return new MatTableDataSource<MaloupeKeyword>(keywords);
    });

    openKeywordsPositionModal(maloupeKeyword: MaloupeKeyword): void {
        this._dialog
            .open(RankingCompetitorsKeywordListComponent, {
                data: {
                    keyword: maloupeKeyword.text,
                    restaurantIndex: maloupeKeyword.keyword.googlePosition - 1,
                    restaurantsRankingList: maloupeKeyword.keyword.competitorsGooglePositions,
                },
                height: '520px',
                width: '750px',
                panelClass: 'malou-dialog-panel',
            })
            .afterClosed()
            .subscribe(() => {
                this._diagnosticHttpService
                    .trackDiagnosticAction$({
                        identity: getUserIdentityFromLocalStorage() ?? '',
                        eventName: HeapEventName.MALOUPE_TRACKING_CLICK_ON_CHECK_KEYWORDS_COMPETITORS,
                        receiverEmail: this.diagnosticData().receiverEmail,
                        diagnosticIds: this.diagnosticData().diagnostic.id,
                    })
                    .subscribe();
            });
    }
}
